import { useHistory } from "react-router-dom";
import { useDot } from "context/automatonContext";
import { useUser } from "context/userContext";
import simulationUtils from "views/Simulate/formatList";
import { formatPath } from "utils/formatPath";
import { MACHINE_TYPES } from "variables/machineVariables";
import DataService from "services/DataService";

/**
 * @typedef {Object} SimulationStep
 * @property {number} stepId
 * @property {{ name: string }} actualState
 */

/**
 * @typedef {Object} MachineSimulationData
 * @property {SimulationStep[]} tape
 * @property {string} wordTape
 * @property {Object[]} finalStates
 * @property {Object} initialState
 * @property {boolean} isAccept
 * @property {string[]} symbolsExternFromAlphabet
 * @property {boolean} computationInterrupted
 */

/**
 * @template T
 * @typedef {Object} ApiResponse
 * @property {T} data
 */

/**
 * Hook responsável por processar resultados de simulação
 * e redirecionar o usuário para a tela apropriada.
 *
 * Suporta:
 * - MACHINE: simulação de autômatos
 * - GRAMMAR: simulação de derivações
 *
 * Efeitos colaterais:
 * - Atualiza estados globais
 * - Pode realizar chamadas ao backend
 * - Executa navegação
 *
 * @returns {{
 *   handleRedirect: (
 *     response: ApiResponse<MachineSimulationData> | MachineSimulationData,
 *     dotCode?: string,
 *     type?: "MACHINE" | "GRAMMAR",
 *     task?: any
 *   ) => void
 * }}
 */
export const useSimulationRedirect = () => {
  const history = useHistory();
  const { credential } = useUser();

  const {
    setResponseSimulate,
    setDataTreeDerivation,
    setIsSimulating,
    setIsMultiPathSimulating,
    setCurrentComputationLevel,
    setSimulationPath,
    setSimulationIndex,
    automatonType,
    setDot,
  } = useDot();

  /**
   * Normaliza resposta (Axios ou objeto direto)
   *
   * @template T
   * @param {ApiResponse<T> | T} response
   * @returns {T}
   */
  const extractData = (response) => {
    if (response && response.data !== undefined) {
      return response.data;
    }
    return response;
  };

  /**
   * Ordena a fita por stepId
   *
   * @param {SimulationStep[]} tape
   * @returns {SimulationStep[]}
   */
  const sortTape = (tape) => {
    if (!tape) {
      return [];
    }

    return [...tape].sort(function (a, b) {
      return a.stepId - b.stepId;
    });
  };

  /**
   * Verifica se o autômato é AFD
   *
   * @param {string} dotCode
   * @returns {Promise<boolean>}
   */
  const checkIfAFD = async (dotCode) => {
    if (
      automatonType === MACHINE_TYPES.AP ||
      automatonType === MACHINE_TYPES.ALL ||
      automatonType === MACHINE_TYPES.MT
    ) {
      return false;
    }

    try {
      const config = { headers: { Authorization: credential } };
      const response = await DataService.seeIfAFDAutomaton(dotCode, config);

      if (response && response.data !== undefined) {
        return response.data;
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  /**
   * Processa dados de simulação de autômatos
   *
   * @param {MachineSimulationData} data
   * @param {string} dotCode
   * @returns {Promise<void>}
   */
  const redirectToMachine = async (data, dotCode) => {
    if (dotCode !== null && dotCode !== undefined) {
      setDot(dotCode);
    }

    const tape = data.tape;
    const wordTape = data.wordTape;
    const finalStates = data.finalStates;
    const initialState = data.initialState;
    const isAccept = data.isAccept;
    const symbolsExternFromAlphabet = data.symbolsExternFromAlphabet;
    const computationInterrupted = data.computationInterrupted;

    const sortedTape = sortTape(tape);

    setResponseSimulate({
      responseSimulateAutomata: {
        word: wordTape,
        tape: sortedTape,
        finalStates: finalStates,
        initialState: initialState,
        isAccept: isAccept,
        symbolsExternFromAlphabet: symbolsExternFromAlphabet,
        computationInterrupted: computationInterrupted,
      },
    });

    // Derivação em árvore
    setDataTreeDerivation(simulationUtils.formatList(sortedTape));

    const isAFD = await checkIfAFD(dotCode);

    if (sortedTape.length > 0) {
      if (!isAFD) {
        setIsSimulating(false);
        setIsMultiPathSimulating(true);
        setSimulationPath([]);
        setSimulationIndex(0);
        setCurrentComputationLevel(0);
      } else {
        const linearPath = formatPath(sortedTape, isAccept, finalStates);

        const statePath = linearPath.map(function (step) {
          return step.actualState.name;
        });

        setResponseSimulate(function (prev) {
          return {
            ...prev,
            responseSimulateAutomata: {
              ...prev.responseSimulateAutomata,
              tape: linearPath,
            },
          };
        });

        setIsMultiPathSimulating(false);
        setIsSimulating(true);
        setSimulationPath(statePath);
        setSimulationIndex(0);
      }

      if (history.location.pathname !== "/user/automaton") {
        history.push("/user/automaton");
      }
    } else if (history.location.pathname !== "/user/responseAutomaton") {
      history.push("/user/responseAutomaton");
    }
  };

  /**
   * Processa a resposta da simulação e redireciona
   *
   * @param {ApiResponse<MachineSimulationData> | MachineSimulationData} response
   * @param {string} dotCode
   * @param {"MACHINE"|"GRAMMAR"} type
   * @param {Object} task
   */
  const handleRedirect = (
    response,
    dotCode = null,
    type = "MACHINE",
    task = null
  ) => {
    if (!response) {
      return;
    }

    const data = extractData(response);

    if (type === "GRAMMAR") {
      if (data && data.steps) {
        setDataTreeDerivation(data.steps);
      }

      const statePayload = {
        simulationResults: data,
        activeTask: task,
      };

      const path = "/user/simulateGrammar";

      if (history.location.pathname === path) {
        history.replace({
          pathname: path,
          state: statePayload,
        });
      } else {
        history.push({
          pathname: path,
          state: statePayload,
        });
      }

      return;
    }

    if (data && data.tape) {
      redirectToMachine(data, dotCode);
    }
  };

  return { handleRedirect };
};
